/**
 * Campaign Service - Supabase queries for email marketing campaigns
 * Handles CRUD for campaigns, recipients, and analytics
 */
import { supabase } from './supabase';

// ─── CAMPAIGNS ───

/** Fetch all campaigns (ordered by newest first) */
export async function fetchCampaigns() {
  const { data, error } = await supabase
    .from('email_campaigns')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/** Fetch a single campaign by ID */
export async function fetchCampaign(id) {
  const { data, error } = await supabase
    .from('email_campaigns')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

/** Create a new campaign */
export async function createCampaign(campaign) {
  const { data, error } = await supabase
    .from('email_campaigns')
    .insert({
      name: campaign.name,
      subject: campaign.subject,
      html_content: campaign.htmlContent,
      text_content: campaign.textContent || null,
      from_name: campaign.fromName || 'Errigal Country House Hotel',
      from_email: campaign.fromEmail || 'events@errigalhotel.com',
      reply_to: campaign.replyTo || 'events@errigalhotel.com',
      target_tags: campaign.targetTags || [],
      status: 'draft',
      created_by: campaign.createdBy || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Update a campaign */
export async function updateCampaign(id, updates) {
  const dbUpdates = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.subject !== undefined) dbUpdates.subject = updates.subject;
  if (updates.htmlContent !== undefined) dbUpdates.html_content = updates.htmlContent;
  if (updates.textContent !== undefined) dbUpdates.text_content = updates.textContent;
  if (updates.fromName !== undefined) dbUpdates.from_name = updates.fromName;
  if (updates.fromEmail !== undefined) dbUpdates.from_email = updates.fromEmail;
  if (updates.replyTo !== undefined) dbUpdates.reply_to = updates.replyTo;
  if (updates.targetTags !== undefined) dbUpdates.target_tags = updates.targetTags;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.scheduledAt !== undefined) dbUpdates.scheduled_at = updates.scheduledAt;

  const { data, error } = await supabase
    .from('email_campaigns')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Delete a campaign */
export async function deleteCampaign(id) {
  const { error } = await supabase
    .from('email_campaigns')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ─── RECIPIENTS ───

/** Add recipients to a campaign (from client list) */
export async function addRecipientsFromClients(campaignId, clients) {
  // First check unsubscribes
  const { data: unsubs } = await supabase
    .from('email_unsubscribes')
    .select('email');
  const unsubEmails = new Set((unsubs || []).map(u => u.email.toLowerCase()));

  // Filter out unsubscribed and those without email
  const validClients = clients.filter(c =>
    c.email && c.email.trim() && !unsubEmails.has(c.email.toLowerCase())
  );

  if (validClients.length === 0) return { added: 0, skipped: clients.length };

  const rows = validClients.map(c => ({
    campaign_id: campaignId,
    client_id: c.id,
    recipient_name: c.name,
    recipient_email: c.email,
    status: 'pending',
  }));

  const { error } = await supabase
    .from('campaign_recipients')
    .insert(rows);
  if (error) throw error;

  // Update total on campaign
  await supabase
    .from('email_campaigns')
    .update({ total_recipients: validClients.length })
    .eq('id', campaignId);

  return { added: validClients.length, skipped: clients.length - validClients.length };
}

/** Fetch recipients for a campaign */
export async function fetchCampaignRecipients(campaignId) {
  const { data, error } = await supabase
    .from('campaign_recipients')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('recipient_name');
  if (error) throw error;
  return data || [];
}

/** Remove a recipient from a campaign */
export async function removeRecipient(recipientId) {
  const { error } = await supabase
    .from('campaign_recipients')
    .delete()
    .eq('id', recipientId);
  if (error) throw error;
}

// ─── SENDING ───

/** Trigger campaign send via edge function */
export async function sendCampaign(campaignId, attachments = []) {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  const payload = { campaign_id: campaignId };
  if (attachments && attachments.length > 0) {
    payload.attachments = attachments.map(a => ({
      filename: a.filename,
      content: a.content,
      contentType: a.contentType,
    }));
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/send-campaign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseKey}`,
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Failed to send campaign');
  return result;
}

// ─── ANALYTICS ───

/** Fetch click events for a campaign */
export async function fetchCampaignClicks(campaignId) {
  const { data, error } = await supabase
    .from('campaign_clicks')
    .select('*, campaign_recipients(recipient_name, recipient_email)')
    .eq('campaign_id', campaignId)
    .order('clicked_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/** Fetch campaigns a specific client received */
export async function fetchClientCampaigns(clientId) {
  const { data, error } = await supabase
    .from('campaign_recipients')
    .select('*, email_campaigns(id, name, subject, sent_at, status)')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ─── UNSUBSCRIBES ───

/** Check if an email is unsubscribed */
export async function isUnsubscribed(email) {
  const { data } = await supabase
    .from('email_unsubscribes')
    .select('id')
    .eq('email', email.toLowerCase())
    .single();
  return !!data;
}

/** Add an unsubscribe */
export async function addUnsubscribe(email, clientId = null, campaignId = null, reason = null) {
  const { error } = await supabase
    .from('email_unsubscribes')
    .upsert({
      email: email.toLowerCase(),
      client_id: clientId,
      campaign_id: campaignId,
      reason,
    }, { onConflict: 'email' });
  if (error) throw error;
}

/** Fetch all unsubscribes */
export async function fetchUnsubscribes() {
  const { data, error } = await supabase
    .from('email_unsubscribes')
    .select('*')
    .order('unsubscribed_at', { ascending: false });
  if (error) throw error;
  return data || [];
}
